import type { ReactNode } from "react";

export type TrendDirection = "up" | "down" | "flat";

export interface MetricTrend {
  delta: number;
  direction: TrendDirection;
}

export interface MetricCardProps {
  label: string;
  value: string | number;
  unit?: string;
  trend?: MetricTrend;
  icon?: ReactNode;
}

const TREND_GLYPH: Record<TrendDirection, string> = {
  up: "▲",
  down: "▼",
  flat: "—",
};

const TREND_CLASS: Record<TrendDirection, string> = {
  up: "text-fc-good",
  down: "text-fc-bad",
  flat: "text-fc-text-dim",
};

export function MetricCard({
  label,
  value,
  unit,
  trend,
  icon,
}: MetricCardProps) {
  return (
    <div className="flex flex-col gap-2 rounded-fc-md border border-fc-border bg-fc-raised p-4">
      <div className="flex items-center justify-between">
        <span className="text-[12px] font-medium uppercase tracking-wide text-fc-text-dim">
          {label}
        </span>
        {icon ? <span className="text-fc-text-secondary">{icon}</span> : null}
      </div>

      <div className="flex items-baseline gap-2">
        <span
          data-testid="metric-value"
          className="font-mono text-[28px] font-semibold text-fc-text"
        >
          {value}
        </span>
        {unit ? (
          <span className="text-[13px] text-fc-text-dim">{unit}</span>
        ) : null}
      </div>

      {trend ? (
        <span
          data-testid="metric-trend"
          data-direction={trend.direction}
          className={`inline-flex items-center gap-1 text-[12px] ${TREND_CLASS[trend.direction]}`}
        >
          <span aria-hidden="true">{TREND_GLYPH[trend.direction]}</span>
          <span>{trend.delta}</span>
        </span>
      ) : null}
    </div>
  );
}
